/**
 * The bars a replayed chart may see, and the finer series behind them.
 *
 * ## Why this moved out of `App`
 *
 * The slice used to be computed at screen level, from the screen's series. That
 * silently assumed the chart being replayed was showing the screen's symbol —
 * true only while some chart pane followed it. A workspace whose charts are all
 * pinned to their own instruments had no such pane, and replay lost its
 * controls entirely with nothing said about why.
 *
 * Slicing per PANE removes the assumption rather than patching it: a pane
 * replays the series it is actually drawing, whatever symbol that is, and the
 * pinned case stops being a special case. See ADR-free note in
 * `docs/34-workspaces.md`.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Bar } from './api.js';
import type { FineReplayResponse } from '@aegis/api-client';
import { formingBar, type ReplayState } from '../components/ReplayControls.js';
import { api } from './api.js';

/**
 * The bars visible at a replay cursor.
 *
 * Pure, and the whole of the replay's honesty. When stepping intrabar the bar
 * AT the cursor is rebuilt from the finer series — open from the first step,
 * high and low from the steps so far, close from the latest — rather than
 * revealed whole. That is a real partial bar made of real data; interpolating a
 * path inside a completed bar would invent the intrabar sequence, which is
 * precisely what replay exists to withhold.
 */
export function replaySlice(
  bars: readonly Bar[],
  state: ReplayState,
  fine: FineReplayResponse | null,
  barSeconds: number,
): Bar[] {
  if (!state.active) return [...bars];

  const complete = bars.slice(0, state.cursor);
  /* Only `!fine` is load-bearing here — it is what stops the null dereference
     below. Both step checks are REDUNDANT with `formingBar`, which refuses a
     step of zero and a stepsPerBar of one itself; `replay.test.ts` pins both.
     They are kept because they state the condition where it is read rather
     than making it something you have to go and look up, and because "the bar
     has not begun forming" is a fact about the replay rather than about the
     finer series. Mutations removing either are equivalent, and the harness
     records them as such rather than as gaps in the tests. */
  if (state.step === 0 || state.stepsPerBar <= 1 || !fine) return complete;

  const target = bars[state.cursor];
  if (!target) return complete;

  const partial = formingBar(target, fine.bars, state.step, state.stepsPerBar, barSeconds);
  return partial ? [...complete, partial] : complete;
}

/**
 * The finest real bars under a timeframe, fetched only while a replay is on.
 *
 * A second series over the same range, and nobody needs it while watching live.
 * Failure is silent and degrades to bar-by-bar stepping: a replay that refused
 * to start because the fine series was unavailable would be worse than one that
 * steps coarsely.
 */
export function useFineReplay(
  symbol: string, timeframe: string, active: boolean,
): FineReplayResponse | null {
  const [fine, setFine] = useState<FineReplayResponse | null>(null);

  useEffect(() => {
    if (!active || !symbol) { setFine(null); return undefined; }
    let cancelled = false;
    void api.fineReplay(symbol, timeframe)
      .then((f) => { if (!cancelled) setFine(f); })
      .catch(() => { if (!cancelled) setFine(null); });
    return () => { cancelled = true; };
  }, [active, symbol, timeframe]);

  return fine;
}

/** What the controls need to describe the replay they are driving. */
export interface ReplayContext {
  /** Bars available to step through — the pane's own count. */
  readonly total: number;
  /** Epoch seconds of the bar at the cursor, for the badge. */
  readonly barTime: number | null;
  /** Finer bars per chart bar. 1 disables the intrabar toggle. */
  readonly maxSteps: number;
  readonly stepLabel: string;
  /** What the API says about what is and is not simulated. */
  readonly fineNote: string;
}

/** The slice and the context together, memoised for one pane. */
export function useReplaySeries(
  bars: readonly Bar[],
  barSeconds: number,
  symbol: string,
  timeframe: string,
  state: ReplayState,
): { visible: Bar[]; context: ReplayContext } {
  const fine = useFineReplay(symbol, timeframe, state.active);

  const visible = useMemo(
    () => replaySlice(bars, state, fine, barSeconds),
    [bars, state, fine, barSeconds],
  );

  const context = useMemo<ReplayContext>(() => ({
    total: bars.length,
    barTime: visible.at(-1)?.time ?? null,
    maxSteps: fine?.stepsPerBar ?? 1,
    stepLabel: fine?.stepTimeframe ?? '1m',
    fineNote: fine?.note ?? '',
  }), [bars.length, visible, fine]);

  return { visible, context };
}
